import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Seo from '../../components/Seo';
import RichTextEditor from '../../components/RichTextEditor';
import { Field, Spinner, PageLoader } from '../../components/ui';
import { activitiesApi, mediaApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';

const EMPTY = {
  title: '',
  summary: '',
  description: '',
  occurred_on: '',
  image_path: '',
};

export default function ActivityForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    activitiesApi
      .get(id)
      .then((a) => {
        if (!active) return;
        setForm({
          title: a.title || '',
          summary: a.summary || '',
          description: a.description || '',
          occurred_on: a.occurred_on || '',
          image_path: a.image_path || '',
        });
      })
      .catch(() => toast.error('Could not load this activity.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'image');
      fd.append('alt_text', file.name);
      const media = await mediaApi.upload(fd);
      const value = media?.url || media?.path || media?.file_path || '';
      if (!value) throw new Error('Upload succeeded but no URL was returned.');
      set('image_path', value);
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed. You can paste an image path instead.');
    } finally {
      setUploading(false);
    }
  }

  async function save(submitAfter) {
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        summary: form.summary || null,
        description: form.description || null,
        occurred_on: form.occurred_on || null,
        image_path: form.image_path || null,
      };
      const saved = isEdit
        ? await activitiesApi.update(id, payload)
        : await activitiesApi.create(payload);
      if (submitAfter && saved?.id) {
        await activitiesApi.submit(saved.id, 'Submitted for review.');
        toast.success('Activity saved and submitted for review.');
      } else {
        toast.success(isEdit ? 'Activity updated.' : 'Draft saved.');
      }
      navigate('/member/activities');
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || 'Could not save activity.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Seo title={isEdit ? 'Edit Activity' : 'Submit Activity'} />
      <h1 className="font-display text-2xl font-bold">{isEdit ? 'Edit Activity' : 'Submit an Activity'}</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
        className="card space-y-5 p-6"
      >
        <Field label="Title" error={errors.title?.[0]}>
          <input className="input" value={form.title} onChange={(e) => set('title', e.target.value)} required />
        </Field>

        <Field label="Date" hint="When the activity took place." error={errors.occurred_on?.[0]}>
          <input
            type="date"
            className="input"
            value={form.occurred_on}
            onChange={(e) => set('occurred_on', e.target.value)}
          />
        </Field>

        <Field label="Summary" hint="A short one-line teaser shown on cards." error={errors.summary?.[0]}>
          <input
            className="input"
            value={form.summary}
            onChange={(e) => set('summary', e.target.value)}
            placeholder="Brief summary"
          />
        </Field>

        <Field
          label="Description"
          hint="Format text, add links, insert images, and build tables."
          error={errors.description?.[0]}
        >
          <RichTextEditor
            id="activity-description"
            value={form.description}
            onChange={(html) => set('description', html)}
            placeholder="What happened during this activity?"
          />
        </Field>

        <Field label="Featured image" error={errors.image_path?.[0]} hint="Upload an image or paste an existing path.">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input type="file" accept="image/*" onChange={handleImage} className="text-sm" />
            <input
              className="input"
              placeholder="activities/my-image.jpg"
              value={form.image_path}
              onChange={(e) => set('image_path', e.target.value)}
            />
          </div>
          {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
          {form.image_path && (
            <img
              src={assetUrl(form.image_path)}
              alt="Featured preview"
              className="mt-3 aspect-video w-full max-w-sm rounded-lg object-cover shadow-card"
            />
          )}
        </Field>

        <div className="flex flex-wrap gap-3 border-t border-cream-300 pt-5">
          <button type="submit" className="btn-outline" disabled={saving}>
            {saving ? <Spinner className="h-4 w-4" /> : 'Save Draft'}
          </button>
          <button type="button" onClick={() => save(true)} className="btn-primary" disabled={saving}>
            Save &amp; Submit for Review
          </button>
          <button type="button" onClick={() => navigate('/member/activities')} className="btn-ghost ml-auto">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
